import { createBrowserRouter, redirect, type RouteObject } from 'react-router';

import {
  ADMIN_AUDIT_PATH,
  ADMIN_BATCHES_PATH,
  ADMIN_COURSES_PATH,
  ADMIN_COURSE_GROUP_SEGMENT,
  ADMIN_GROUPS_PATH,
  ADMIN_GROUP_SEGMENT,
  ADMIN_PATH,
  ADMIN_PREINVITES_PATH,
  ADMIN_QA_PATH,
  ADMIN_QA_QUESTION_SEGMENT,
  ADMIN_REQUESTS_PATH,
  ADMIN_STUDENTS_PATH,
  ADMIN_STUDENT_SEGMENT,
  ADMIN_SYSTEM_PATH,
  COURSES_PATH,
  COURSE_STUDIO_SEGMENT,
  COURSE_MATERIAL_SEGMENT,
  COURSE_SEGMENT,
  HOME_PATH,
  PROFILE_PATH,
  QA_PATH,
  QA_QUESTION_SEGMENT,
} from './paths';
import { RouteErrorRoute } from './routes/RouteErrorRoute';

/** The two Claude Design frames' route ids — how the table's tests tell the pathless frames apart. */
export const DESIGN_ADMIN_FRAME_ID = 'design-admin';
export const DESIGN_STUDENT_FRAME_ID = 'design-student';

/**
 * Administrator addresses whose screens are gone (OD-D2.10b); each, with or without an identifier,
 * redirects to Admin Home.
 */
export const RETIRED_ADMIN_PATHS: readonly string[] = Object.freeze(
  [ADMIN_REQUESTS_PATH, ADMIN_AUDIT_PATH, ADMIN_BATCHES_PATH, ADMIN_SYSTEM_PATH].flatMap((path) => [path, `${path}/:id`]),
);

/**
 * The route table — T007, now a design-only application.
 *
 * Every screen is a Claude Design page (`DESIGN.md` § Method step 3): the administrator frame, the
 * student frame, and the entry/catch-all frame. No route loads an application stylesheet. Hosting
 * rewrites every path to `index.html` (`infra/firebase.json`), so the catch-all below is what turns a
 * mistyped address into the Access page's „not found“ state rather than an empty document.
 *
 * Every screen is loaded lazily, so a signed-out visitor downloads the Access page and nothing else.
 * The table is exported separately from the router so it can be asserted directly, without a browser
 * history existing.
 */
/** While a screen's code first downloads nothing is drawn — each frame paints its own loading state. */
function LoadingNothing() {
  return null;
}

export const routes: readonly RouteObject[] = Object.freeze([
  {
    /*
     * The administrator Claude Design frame (`DESIGN.md` § Method). Pathless, so
     * its screens keep their production addresses; it renders the Access page for every session the
     * server did not let in as an administrator (`DesignGate`).
     */
    id: DESIGN_ADMIN_FRAME_ID,
    lazy: async () => ({ Component: (await import('../design/app/admin-qa/AdminQaScreen')).AdminDesignFrame }),
    ErrorBoundary: RouteErrorRoute,
    HydrateFallback: LoadingNothing,
    children: [
      {
        // Home (OD-H5): the bare administrator address is the design's Admin Home, where an
        // administrator also lands after sign-in (`HomeScreen`).
        path: ADMIN_PATH.slice(1),
        lazy: async () => ({ Component: (await import('../design/app/admin-home/AdminHomeScreen')).AdminHomeScreen }),
      },
      {
        // The Q&A workspace and one selected question — the same screen, so a cold permalink loads
        // the question itself rather than a second screen that needs the queue first (D-ROUTE).
        path: ADMIN_QA_PATH.slice(1),
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../design/app/admin-qa/AdminQaScreen')).AdminQaScreen }),
          },
          {
            path: ADMIN_QA_QUESTION_SEGMENT,
            lazy: async () => ({ Component: (await import('../design/app/admin-qa/AdminQaScreen')).AdminQaScreen }),
          },
        ],
      },
      {
        // The student register and one student — one screen with an optional segment, so choosing a
        // row keeps the page's filters and scroll; an invited person is `?invite=<token>` (OD-S1).
        path: `${ADMIN_STUDENTS_PATH.slice(1)}/${ADMIN_STUDENT_SEGMENT}?`,
        lazy: async () => ({ Component: (await import('../design/app/admin-students/AdminStudentsScreen')).AdminStudentsScreen }),
      },
      {
        // OD-D2.10b: the pre-invitations live in Students „მოწვეული“; the old address leads there.
        path: ADMIN_PREINVITES_PATH.slice(1),
        lazy: async () => ({ Component: (await import('../design/app/admin-students/AdminStudentsScreen')).PreInvitesRedirect }),
      },
      {
        // The group register and one group with its tab — one screen with optional segments, so
        // choosing a row or a tab keeps the page's filters and scroll (OD-G1…G16).
        path: `${ADMIN_GROUPS_PATH.slice(1)}/${ADMIN_GROUP_SEGMENT}`,
        lazy: async () => ({ Component: (await import('../design/app/admin-groups/AdminGroupsScreen')).AdminGroupsScreen }),
      },
      {
        // The old course-nested group address leads to the same group on the Groups page.
        path: `${ADMIN_COURSES_PATH.slice(1)}/${ADMIN_COURSE_GROUP_SEGMENT}`,
        lazy: async () => ({ Component: (await import('../design/app/admin-groups/AdminGroupsScreen')).LegacyGroupRedirect }),
      },
      {
        // *(AD-6)* The course register with its wizard (`?create=1`, `?edit=<id>`, `?delete=<id>`) —
        // `design/app/admin-courses`.
        path: ADMIN_COURSES_PATH.slice(1),
        lazy: async () => ({ Component: (await import('../design/app/admin-courses/AdminCoursesScreen')).AdminCoursesScreen }),
      },
      {
        // *(OD-CS1)* The Course Studio: one course and its tab, the selection in the query; an older tab
        // name leads to its Studio tab — `design/app/admin-course-studio`.
        path: `${ADMIN_COURSES_PATH.slice(1)}/${COURSE_STUDIO_SEGMENT}`,
        lazy: async () => ({ Component: (await import('../design/app/admin-course-studio/CourseStudioScreen')).CourseStudioScreen }),
      },
    ],
  },
  {
    /*
     * The student Claude Design frame: pathless like the administrator one, rendering the Access page
     * for every session the server did not let in (`DesignGate audience="student"`). The profile, and
     * the course library and one course as **one** screen (an optional segment, so moving between the
     * two keeps the page — OD-SC11). One material is the Reader page, at its complete address
     * (OD-SR1…SR15). The Q&A queue and one question (FR-SQA-06) are likewise one screen with an
     * optional segment (OD-SQ16).
     */
    id: DESIGN_STUDENT_FRAME_ID,
    lazy: async () => ({ Component: (await import('../design/app/student-courses/StudentCoursesScreen')).StudentDesignFrame }),
    ErrorBoundary: RouteErrorRoute,
    HydrateFallback: LoadingNothing,
    children: [
      {
        path: PROFILE_PATH.slice(1),
        lazy: async () => ({ Component: (await import('../design/app/student-profile/StudentProfileScreen')).StudentProfileScreen }),
      },
      {
        path: `${COURSES_PATH.slice(1)}/${COURSE_SEGMENT}?`,
        lazy: async () => ({ Component: (await import('../design/app/student-courses/StudentCoursesScreen')).StudentCoursesScreen }),
      },
      {
        // *(T062-B, D9)* Complete — course, section and material — because that is what the protected
        // read operations take and what a cold link therefore has to carry.
        path: `${COURSES_PATH.slice(1)}/${COURSE_MATERIAL_SEGMENT}`,
        lazy: async () => ({ Component: (await import('../design/app/student-reader/StudentReaderScreen')).StudentReaderScreen }),
      },
      {
        path: `${QA_PATH.slice(1)}/${QA_QUESTION_SEGMENT}?`,
        lazy: async () => ({ Component: (await import('../design/app/student-qa/StudentQaScreen')).StudentQaScreen }),
      },
    ],
  },
  {
    /*
     * The entry address and the catch-all, in a design frame of their own: `/` sends an approved
     * account to its daily surface (the administrator to Admin Home, a student to the courses) and shows the
     * Access page for every other session (`HomeScreen`); an unknown address is the Access page's
     * „not found“ state (`NotFoundScreen`).
     */
    path: HOME_PATH,
    ErrorBoundary: RouteErrorRoute,
    HydrateFallback: LoadingNothing,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../design/app/access')).HomeScreen }),
      },
      // OD-D2.10b: registration requests are handled on Admin Home; the action history, the batch
      // pages and the system status are not part of the product any more. Their old addresses —
      // bookmarks, links in earlier notifications — lead to Admin Home rather than to „not found“.
      ...RETIRED_ADMIN_PATHS.map((path) => ({ path: path.slice(1), loader: () => redirect(ADMIN_PATH) })),
      {
        path: '*',
        lazy: async () => ({ Component: (await import('../design/app/access')).NotFoundScreen }),
      },
    ],
  },
]);

export function createRouter() {
  return createBrowserRouter([...routes]);
}
